import React from 'react';

import { createRoot } from 'react-dom/client';
import axios from "axios";

// Bootstrap 5 CSS (from npm - single source of truth)
import 'bootstrap/dist/css/bootstrap.min.css';

// DataTables Bootstrap 5 integration CSS
// import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
// import 'datatables.net-buttons-bs5/css/buttons.bootstrap5.min.css';
// import 'datatables.net-responsive-bs5/css/responsive.bootstrap5.min.css';

// Font Awesome
import '@fortawesome/fontawesome-free/css/all.min.css';

// Bootstrap Icons
// import 'bootstrap-icons/font/bootstrap-icons.css'; // Removed to avoid loading two heavy icon libraries
             
// MDB (Material Design Bootstrap) - may override some Bootstrap styles
// import 'mdb-react-ui-kit/dist/css/mdb.min.css';

// Custom styles last (highest priority)
import './index.css'

import config from './config.js'
import store from './store'
import { setNotificationCount } from './reducer'

// ===== Notification Count Header Extractor =====
export const updateNotificationCountFromHeaders = (headers) => {
    if (!headers) return;
    try {
        let notifHeader = null;
        if (typeof headers.get === 'function') {
            notifHeader = headers.get('x-notification-count') || headers.get('X-Notification-Count');
        } else if (typeof headers === 'object') {
            notifHeader = headers['x-notification-count'] ?? headers['X-Notification-Count'];
        }
        if (notifHeader !== undefined && notifHeader !== null) {
            const count = parseInt(notifHeader, 10);
            if (!isNaN(count)) {
                store.dispatch(setNotificationCount(count));
            }
        }
    } catch (e) {
        // silent
    }
};

// ===== Navigation Request Cancellation =====
// Cancel all in-flight requests when navigating to a new page
let navigationController = new AbortController();

window.cancelNavigationRequests = function () {
    navigationController.abort();
    navigationController = new AbortController();
};

window.getNavigationSignal = function () {
    return navigationController.signal;
};

// ===== CRITICAL: Override native fetch FIRST, before any library loads =====
// Global configuration for authenticated requests
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Accept'] = 'application/json';

// Add request interceptor for token authentication + navigation abort signal
axios.interceptors.request.use(
    config => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        const lang = localStorage.getItem('lang') || 'en';
        config.headers.lang = lang;

        // Attach navigation abort signal so requests are cancelled on page change
        if (!config.signal) {
            config.signal = window.getNavigationSignal();
        }

        return config;
    },
    error => Promise.reject(error)
);

// Add response interceptor for error handling and notification count header
axios.interceptors.response.use(
    response => {
        if (response?.headers) {
            updateNotificationCountFromHeaders(response.headers);
        }
        return response;
    },
    error => {
        if (error?.response?.headers) {
            updateNotificationCountFromHeaders(error.response.headers);
        }
        // Silently swallow cancelled requests (from page navigation)
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
            return new Promise(() => {}); // never resolves — silently ignored
        }
        if (error.response?.status === 401) {
            console.error('[Auth] Request failed with 401, token may be expired');
            // Optionally redirect to login or refresh token here
        }
        return Promise.reject(error);
    }
);

// This ensures Form.io, axios, and all other libs use our authenticated fetch
const _origFetch = window.fetch.bind(window);
const readCookie = (name) => {
    try {
        const match = document.cookie.match(new RegExp('(^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
        return match ? decodeURIComponent(match[2]) : null;
    } catch(e){ return null; }
};

window.fetch = function(input, init = {}) {
    try {
        const urlStr = typeof input === 'string' ? input : (input && input.url) || '';
        const backendOrigin = config.BackendUrl ? new URL(config.BackendUrl).origin : '';
        const isBackendRequest = backendOrigin && urlStr.startsWith(backendOrigin);
        
        if (!isBackendRequest) {
            // Non-backend requests: use original fetch as-is
            return _origFetch(input, init);
        }

        // Backend requests: add credentials + auth headers
        console.log('[fetch wrapper] Backend request:', urlStr);
        
        // Special logging for Ticket detail requests
        if (urlStr.includes('/Ticket/') && urlStr.includes('include=')) {
            console.log('[fetch wrapper] 🎫 TICKET DETAIL REQUEST:', {
                url: urlStr,
                method: init.method || 'GET',
                includeParams: urlStr.split('include=')[1]?.split('&')[0],
                timestamp: new Date().toISOString(),
                stackTrace: new Error().stack
            });
        }
        
        const headers = new Headers(init.headers || {});
        
        if (!headers.has('Accept')) headers.set('Accept', 'application/json');
        
        const lang = (typeof localStorage !== 'undefined' && localStorage.getItem('lang')) || 'en';
        if (!headers.has('lang')) headers.set('lang', lang);
        
        const token = typeof localStorage !== 'undefined' && localStorage.getItem('token');
        if (token && !headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        
        const xsrf = readCookie('XSRF-TOKEN');
        if (xsrf && !headers.has('X-XSRF-TOKEN')) {
            headers.set('X-XSRF-TOKEN', xsrf);
        }
        
        if (!headers.has('X-Requested-With')) {
            headers.set('X-Requested-With', 'XMLHttpRequest');
        }

        // Use existing signal or attach navigation signal for auto-cancellation
        const signal = init.signal || window.getNavigationSignal();

        return _origFetch(input, {
            ...init,
            credentials: 'include',
            mode: 'cors',
            headers,
            signal,
        }).then(response => {
            try {
                if (response?.headers) {
                    updateNotificationCountFromHeaders(response.headers);
                }
            } catch (err) {
                // ignore
            }
            return response;
        });
    } catch (e) {
        console.warn('[fetch wrapper] Error:', e);
        return _origFetch(input, init);
    }
};


// Global XHR wrapper to enforce withCredentials and auth headers for backend calls
(function(){
    try {
        const backendOrigin = config.BackendUrl ? new URL(config.BackendUrl).origin : '';
        const readCookie = (name) => {
            try {
                const match = document.cookie.match(new RegExp('(^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
                return match ? decodeURIComponent(match[2]) : null;
            } catch(e){ return null; }
        };
        const origOpen = XMLHttpRequest.prototype.open;
        const origSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function(method, url, ...rest){
            try { 
                this._isBackend = backendOrigin && url.startsWith(backendOrigin);
                this._url = url;  // Store URL for logging in send
                this._method = method;  // Store method for logging
            } catch(e){ 
                this._isBackend = false; 
            }
            return origOpen.call(this, method, url, ...rest);
        };
        XMLHttpRequest.prototype.send = function(body){
            if (this._isBackend) {
                try {
                    // Log XHR requests to Ticket endpoints
                    if (this._url && this._url.includes('/Ticket/') && this._url.includes('include=')) {
                        console.log('[XHR wrapper] 🎫 TICKET DETAIL REQUEST:', {
                            url: this._url,
                            method: this._method || 'GET',
                            includeParams: this._url.split('include=')[1]?.split('&')[0],
                            timestamp: new Date().toISOString(),
                            stackTrace: new Error().stack
                        });
                    }
                    
                    this.withCredentials = true;
                    const lang = (typeof localStorage !== 'undefined' && localStorage.getItem('lang')) || 'en';
                    this.setRequestHeader('lang', lang);
                    const token = (typeof localStorage !== 'undefined' && localStorage.getItem('token'));
                    if (token) this.setRequestHeader('Authorization', 'Bearer ' + token);
                    const xsrf = readCookie('XSRF-TOKEN');
                    if (xsrf) this.setRequestHeader('X-XSRF-TOKEN', xsrf);
                    this.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
                    if (!this._acceptSet) { this.setRequestHeader('Accept', 'application/json'); this._acceptSet = true; }
                } catch(e){ /* silent */ }
            }
            return origSend.call(this, body);
        };
        console.log('[XHR wrapper] Installed');
    } catch(e){
        console.warn('[XHR wrapper] Failed to install', e);
    }
})();

import App from './App';
import * as serviceWorker from './serviceWorker';

// ===== FORM.IO CONFIGURATION =====
// Configure FormIO to reduce multiple requests

    // Configure essential FormIO authentication and headers
    // This handles: language headers, CSRF tokens, content negotiation
    // Token-based auth is commented out in the module pending removal verification
    import { configureFormIOAuth } from './auth/formio-auth.js';
    configureFormIOAuth();
    
    // Setup calculateValue interceptor to modernize legacy form code
    // This adds authentication to fetch calls and provides proper field updates
    import { setupCalculateValueInterceptor } from './formio/calculateValueInterceptor.js';
    setupCalculateValueInterceptor();
    
    // FormIO optimization code has been moved to src/optimizations/formio-optimizations.js
    // This includes: malformed URL detection, request deduplication, caching, and component initialization staggering
    import { applyFormIOOptimizations } from './optimizations/formio-optimizations.js';
    applyFormIOOptimizations();
import LogoutRequestHandler from "./Components/Auth/LogoutRequestHandler";
axios.defaults.withCredentials = true;
// Propagate auth token by default on axios when available
try {
    const startupToken = localStorage.getItem('token');
    if (startupToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${startupToken}`;
    }
} catch (e) {
    // ignore
}
    if(config.hasOwnProperty("allowRequestIntercepts")){
    if(config.allowRequestIntercepts)
    axios.interceptors.response.use(
        function(response) {
            // Call was successful, don't do anything special.
            return response;
        },
        function (error) {
            // Silently swallow cancelled requests (from page navigation)
            if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
                return new Promise(() => {});
            }
            if(error.config.url.includes(config.BackendUrl) && typeof error.response !== "undefined"){
                console.log(error.config.url);
            switch (error.response.status) {
                case 401: // Not logged in
                case 419: // Session expired
                case 503: // Down for maintenance
                    // Bounce the user to the login screen with a redirect back
                    LogoutRequestHandler();
                    return Promise.reject(error);
                case 500:
                    alert('Oops, something went wrong!  The team have been notified.');
                    return Promise.reject(error);
                default:
                    // Allow individual requests to handle other errors
                    return Promise.reject(error);
            }
            }else {
                return Promise.reject(error);

            }
        });
}

// Always attach Authorization/lang headers from localStorage for axios requests
// (Removed redundant request interceptor that duplicated lines 47-65)

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<App />);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

